// apps/platform/src/components/ConfirmDialog.tsx
//
// Lightweight in-app confirm replacement for window.confirm(). Matches the
// visual language of EditTenantIdentityModal — backdrop + centered card +
// Cancelar / primary action buttons. Variant "danger" tints the primary
// button red for destructive actions.

import type { CSSProperties, ReactNode } from "react";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../lib/tokens";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** A conclusion that is not dismiss. Backdrop and Cancelar stay on `onCancel`. */
  secondaryLabel?: string;
  onSecondary?: () => void;
  variant?: "default" | "danger";
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open, title, message, confirmLabel = "Confirmar", cancelLabel = "Cancelar",
  secondaryLabel, onSecondary,
  variant = "default", loading = false, onConfirm, onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const submitStyle: CSSProperties = variant === "danger"
    ? { ...s.submitBtn, background: colors.error }
    : s.submitBtn;

  return (
    <div style={s.backdrop} onClick={loading ? undefined : onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={s.card}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={s.title}>{title}</h2>
        <div style={s.message}>{message}</div>
        <div style={s.actions}>
          <button type="button" onClick={onCancel} disabled={loading} style={s.cancelBtn}>
            {cancelLabel}
          </button>
          {secondaryLabel && onSecondary && (
            <button type="button" onClick={onSecondary} disabled={loading} style={s.secondaryBtn}>
              {secondaryLabel}
            </button>
          )}
          <button type="button" onClick={onConfirm} disabled={loading} style={submitStyle}>
            {loading ? "Aguarde..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(14, 17, 22, 0.4)",
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.lg,
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    boxShadow: shadow.lg,
    padding: space.xxl,
    width: 460,
    maxWidth: "100%",
  },
  title: {
    margin: `0 0 ${space.md}px 0`,
    fontSize: fontSize.lg,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  },
  message: {
    fontSize: fontSize.base,
    color: colors.stone,
    lineHeight: 1.5,
    marginBottom: space.lg,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: space.sm,
    justifyContent: "flex-end",
  },
  cancelBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.fog}`,
    background: "transparent",
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.ink}`,
    background: colors.white,
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  submitBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
};
